import { LoadingButton } from "@mui/lab";
import { Box, Button, Card, CardContent, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { Link } from 'react-router-dom';
import { useEffect, useState } from "react";
import UploadIcon from '@mui/icons-material/Upload';
import { FormProvider, RHFDateRangePickerField, RHFFileField, RHFSelectField, RHFTextField } from "../../common/hook-form";
import RHFMultipleSelectField from "../../common/hook-form/RHFMultipleSelectField";
import { useForm } from "react-hook-form";
import { apiConstants, getGeneric, post } from "../../common/constants";
import { useSnackbar } from "notistack";
import PageTitle from "../../components/PageTitle";
import { useGetPtlPurQuery } from "../../redux/commonApiCalls";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { formatDate, maskAddress, parseDateAndTime, shouldMaskData } from "../../helpers/UtilityHelper";
import { postGeneric } from "../../common/constants";
import MUIModal from '../../components/MuiModal';
import { CourierFilter, MerchantFilterCached } from "../../common/ProshipFormComponents";
import { useNavigate } from "react-router-dom";
import { Cancel, Visibility, CheckCircle, ErrorOutline, LocalShipping } from "@mui/icons-material";
import MarkPickupFailedModal from "./MarkPickupFailedModal";
import DownloadSampleFile from "../../components/DownloadSampleFile";
import _ from "lodash";
import { pickupRequestStatus } from "../../common/util";
import Loader from "../../common/Loader";
import { useSelector } from "react-redux";
import jwt_decode from "jwt-decode";
import { getToken } from "../../helpers/ReduxHelpers";
import AddIcon from '@mui/icons-material/Add';
import { subDays } from "date-fns";
import StatusChip from "../../common/StatusChip";
import PaymentBadge from "../../common/PaymentBadge";
import OrderRowActions from "../../common/OrderRowActions";
import { FieldLabel, FilterField, FilterDateField } from "../../common/FilterBar";

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold, ellipsis }) => (
    <Box sx={{
        fontSize: 12.5, lineHeight: 1.4,
        color: muted ? 'text.secondary' : 'text.primary',
        fontWeight: bold ? 600 : 400,
        ...(ellipsis ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
    }}>
        {children}
    </Box>
);

const CellStack = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5 }}>
        {children}
    </Box>
);

function PtlStatusBadge({ status }) {
    if (!status) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>;
    const map = {
        PICKUP_REQUESTED: { bg: '#dbeafe', fg: '#1d4ed8' },
        OPEN:             { bg: '#e0f2f1', fg: '#00695c' },
        FAILED:           { bg: '#fef2f2', fg: '#dc2626' },
        CANCELLED:        { bg: '#f9fafb', fg: '#6b7280' },
        COURIER_ALLOCATED:{ bg: '#dcfce7', fg: '#15803d' },
        PICKUP_COMPLETED: { bg: '#dcfce7', fg: '#166534' },
        PICKUP_FAILED:    { bg: '#fef2f2', fg: '#b91c1c' },
    };
    const p = map[status] || { bg: '#eff6ff', fg: '#1d4ed8' };
    return (
        <Box component="span" sx={{
            display: 'inline-block', px: '7px', py: '2px', borderRadius: '4px',
            fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
            bgcolor: p.bg, color: p.fg,
        }}>
            {status.replace(/_/g, ' ')}
        </Box>
    );
}

function AllocationBadge({ value }) {
    return (
        <Box component="span" sx={{
            display: 'inline-block', px: '6px', py: '2px', borderRadius: '3px',
            fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
            bgcolor: value ? '#f0fdf4' : '#fef2f2',
            color: value ? '#16a34a' : '#dc2626',
            border: `1px solid ${value ? '#bbf7d0' : '#fecaca'}`,
        }}>
            {value ? 'Yes' : 'No'}
        </Box>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function PtlPickupRequests() {
    const { enqueueSnackbar } = useSnackbar();
    const [sizePerPage, setSizePerPage] = useState(10);
    const [page, setPage] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const methods = useForm();
    const [selectedReferences, setSelectedReferences] = useState([]);
    const [isCourierFilterOpen, setIsCourierFilterOpen] = useState(false);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [andFilters, setAndFilters] = useState([]);
    const [isExecuting, setIsExecuting] = useState(false);
    const [failModalRow, setFailModalRow] = useState(null);
    const [confirmCompleteRow, setConfirmCompleteRow] = useState(null);
    const [idMerchant, setIDMerchant] = useState();
    const [summaryData, setSummaryData] = useState({
        total: 0, pickupRequested: 0, open: 0,
        failed: 0, cancelled: 0, courierAllocated: 0, completed: 0, pickupFailed: 0
    });
    const [summaryLoading, setSummaryLoading] = useState(false);
    const navigate = useNavigate();
    const { report } = useSelector(state => state);
    const { piiAccessAllow } = jwt_decode(getToken());
    const { data: orders = [], isFetching, isLoading, refetch } = useGetPtlPurQuery({
        limit: sizePerPage,
        offset: (page) * sizePerPage,
        andfilter: andFilters
    });

    const columns = [
        {
            field: 'reference',
            headerName: 'Reference', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 160, flex: 1.2,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine bold>{row.reference || '—'}</CellLine>
                    {row.createdDate && <Box sx={{ fontSize: '11px', color: '#9ca3af' }}>{parseDateAndTime(row.createdDate)}</Box>}
                </CellStack>
            ),
        },
        {
            field: 'merchantDetail',
            headerName: 'Merchant', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1,
            renderCell: ({ row }) => <CellLine bold>{row?.merchantDetail?.name || '—'}</CellLine>,
        },
        {
            field: 'courier',
            headerName: 'Courier', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1,
            renderCell: ({ row }) => (
                <CellStack>
                    {row?.courierDetail?.name && <CellLine bold>{row.courierDetail.name}</CellLine>}
                    {row?.courierDetail?.prozoId && <CellLine muted ellipsis>{row.courierDetail.prozoId}</CellLine>}
                    {!row?.courierDetail?.name && <CellLine muted>—</CellLine>}
                </CellStack>
            ),
        },
        {
            field: 'orderReadyDate',
            headerName: 'Ready Date', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 130, flex: 0.9,
            renderCell: ({ row }) => <CellLine muted>{parseDateAndTime(row?.orderReadyDate, row?.orderReadyTime)}</CellLine>,
        },
        {
            field: 'paymentDetails',
            headerName: 'Payment', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 120,
            renderCell: ({ row }) => (
                <CellStack>
                    {row.orderType && <Box sx={{ fontSize: '11px', color: '#6b7280' }}>{row.orderType}</Box>}
                    <PaymentBadge mode={row.paymentType} />
                </CellStack>
            ),
        },
        {
            field: 'warehouseName',
            headerName: 'Warehouse', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 140, flex: 1,
            renderCell: ({ row }) => <CellLine ellipsis>{row.warehouseName || '—'}</CellLine>,
        },
        {
            field: 'delivery',
            headerName: 'Delivery', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 140, flex: 1,
            renderCell: ({ row }) => {
                const masked = shouldMaskData(report, row, piiAccessAllow);
                const cityRegion = masked
                    ? maskAddress(row?.dropoffLocation?.city ?? '', row?.dropoffLocation?.region ?? '')
                    : [row?.dropoffLocation?.city, row?.dropoffLocation?.region].filter(Boolean).join(', ');
                const zip = masked
                    ? maskAddress(row?.dropoffLocation?.zip ?? '')
                    : row?.dropoffLocation?.zip ?? '';
                return (
                    <CellStack>
                        {cityRegion && <CellLine bold>{cityRegion}</CellLine>}
                        {zip && <Box sx={{ fontSize: '11px', color: '#6b7280' }}>{zip}</Box>}
                    </CellStack>
                );
            },
        },
        {
            field: 'boxes',
            headerName: 'Pkgs', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 70,
            renderCell: ({ row }) => {
                const k = row?.dimensions?.reduce((sum, item) => sum + item.count, 0);
                return <Box sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{k ?? '—'}</Box>;
            },
        },
        {
            field: 'weight',
            headerName: 'Weight', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 90,
            renderCell: ({ row }) => (
                <CellLine muted>
                    {row?.totalPackageWeight ? `${(row.totalPackageWeight / 1000).toFixed(2)} kg` : '—'}
                </CellLine>
            ),
        },
        {
            field: 'allocation',
            headerName: 'Auto Alloc.', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 100,
            renderCell: ({ row }) => <AllocationBadge value={row.isAutoAllocateWhileUpload} />,
        },
        {
            field: 'sourceType',
            headerName: 'Source', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 110,
            renderCell: ({ row }) => <CellLine muted>{row?.source?.sourceType || '—'}</CellLine>,
        },
        {
            field: 'status',
            headerName: 'Status', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 160, flex: 1,
            renderCell: ({ row }) => <PtlStatusBadge status={row?.status} />,
        },
        {
            field: 'action2',
            headerName: 'Actions', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 60,
            renderCell: ({ row }) => {
                const actions = [
                    {
                        key: 'view',
                        label: 'View details',
                        icon: <Visibility fontSize="small" />,
                        href: `/ptl-pickup-requests/${row.id}`,
                    },
                ];
                const isClosed = ['CANCELLED', 'PICKUP_COMPLETED', 'PICKUP_FAILED'].includes(row?.status);
                if (!isClosed) {
                    if (row?.status === 'COURIER_ALLOCATED') {
                        actions.push({
                            key: 'request',
                            label: 'Mark Pickup Requested',
                            icon: <LocalShipping fontSize="small" />,
                            onClick: () => handleMarkRequested(row),
                            dividerBefore: true,
                        });
                    }
                    actions.push({
                        key: 'completed',
                        label: 'Mark Pickup Completed',
                        icon: <CheckCircle fontSize="small" />,
                        onClick: () => setConfirmCompleteRow(row),
                        color: 'success',
                        dividerBefore: row?.status !== 'COURIER_ALLOCATED',
                    });
                    actions.push({
                        key: 'failed',
                        label: 'Mark Pickup Failed',
                        icon: <ErrorOutline fontSize="small" />,
                        onClick: () => setFailModalRow(row),
                        color: 'warning',
                    });
                    actions.push({
                        key: 'cancel',
                        label: 'Cancel pickup request',
                        icon: <Cancel fontSize="small" />,
                        onClick: () => handleCancelOrder(row.reference, row.merchantDetail?.key),
                        color: 'error',
                        dividerBefore: true,
                    });
                }
                return <OrderRowActions actions={actions} />;
            },
        },
    ];

    const { handleSubmit, reset } = methods;
    const filterVal = useForm({
        defaultValues: {
            status: '',
            reference: '',
            fromDate: subDays(new Date(), 30),
            toDate: new Date(),
            merchant: '',
            courier: ''
        }
    });
    const pickupStatus = pickupRequestStatus.map((op) => ({ label: _.startCase(op), value: op }));
    const { handleSubmit: handleFilterSubmit, reset: filterReset } = filterVal;

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        let formData = new FormData();
        formData.append('file', data?.file[0]);
        try {
            await post(apiConstants.BULK_PTL_PUR, formData).then((res) => {
                if (res.status === 200 && res.data?.success === true) {
                    setIsSubmitting(false);
                    enqueueSnackbar('Successfully uploaded!', { variant: 'success' });
                    reset();
                } else if (res.status === 200 && res.data?.success === false) {
                    setIsSubmitting(false);
                    enqueueSnackbar(res.data?.messages, { variant: 'error' });
                    reset();
                } else {
                    enqueueSnackbar(`${res.data}`, { variant: 'error' });
                    setIsSubmitting(false);
                    reset();
                }
            });
            reset();
        } catch (err) {
            const message = err.response?.data?.message || err.message;
            enqueueSnackbar(message, { variant: 'error' });
            reset();
            setIsSubmitting(false);
        }
    };

    const autoAllocateFunc = () => {
        setIsExecuting(true);
        postGeneric(apiConstants.AUTO_ALLOCATE_COURIER, { references: selectedReferences }).then(response => {
            if (response.status === 200) enqueueSnackbar(response?.data?.message, { variant: 'success' });
            setSelectedReferences([]);
            setRowSelectionModel([]);
            setIsExecuting(false);
        }).catch((err) => {
            console.log(err);
            const message = err?.response?.data?.message || "Auto Allocation Failed";
            enqueueSnackbar(message, { variant: "error" });
            setSelectedReferences([]);
            setRowSelectionModel([]);
            setIsExecuting(false);
        });
    };

    const handleClose = () => setIsCourierFilterOpen(false);

    const refreshAfterAction = () => {
        refetch();
        fetchSummaryData(andFilters);
    };

    const handleCancelOrder = (orderId, merchantId) => {
        postGeneric(apiConstants.MANUAL_CANCEL_COURIER, { references: [orderId], merchantOid: merchantId })
            .then(response => {
                if (response.status === 200) enqueueSnackbar('Pickup request cancelled successfully', { variant: 'success' });
                refreshAfterAction();
            })
            .catch((err) => {
                console.log(err);
                const message = err?.response?.data?.message || "Something went wrong while cancelling pickup request";
                enqueueSnackbar(message, { variant: "error" });
            });
    };

    // Reads the per-reference result list returned by the close/pickup endpoints and
    // surfaces a partial-failure warning, otherwise the success message.
    const reportBatchResult = (response, successMsg, fallbackMsg) => {
        const results = response?.data || [];
        const failedItem = Array.isArray(results) ? results.find((r) => r && r.success === false) : null;
        if (failedItem) enqueueSnackbar(failedItem.message || fallbackMsg, { variant: 'warning' });
        else enqueueSnackbar(successMsg, { variant: 'success' });
    };

    const handleMarkCompleted = (row) => {
        setIsExecuting(true);
        postGeneric(apiConstants.CLOSE_PICKUP, {
            references: [row.reference], merchantOid: row.merchantDetail?.key, status: 'PICKUP_COMPLETED',
        })
            .then(response => {
                reportBatchResult(response, 'Pickup marked completed', 'Could not mark pickup completed');
                refreshAfterAction();
            })
            .catch((err) => {
                const message = err?.response?.data?.message || "Something went wrong while updating pickup request";
                enqueueSnackbar(message, { variant: "error" });
            })
            .finally(() => setIsExecuting(false));
    };

    const handleMarkRequested = (row) => {
        setIsExecuting(true);
        postGeneric(apiConstants.GENERATE_PICKUP_REQUEST, {
            references: [row.reference], merchantOid: row.merchantDetail?.key,
        })
            .then(response => {
                reportBatchResult(response, 'Pickup requested', 'Could not request pickup');
                refreshAfterAction();
            })
            .catch((err) => {
                const message = err?.response?.data?.message || "Something went wrong while requesting pickup";
                enqueueSnackbar(message, { variant: "error" });
            })
            .finally(() => setIsExecuting(false));
    };

    useEffect(() => {
        const references = orders
            .filter((val) => rowSelectionModel.includes(val.id))
            .map((order) => order.reference);
        setSelectedReferences(references);
    }, [rowSelectionModel, orders]);

    const fetchSummaryData = async (filters = []) => {
        setSummaryLoading(true);
        try {
            const payload = { limit: 10000, offset: 0, "includes": "status", andfilter: filters };
            const response = await postGeneric(apiConstants.PTL_PUR_SEARCH, payload);
            if (response.status === 200 && response.data) {
                const orders = response?.data?.data || [];
                console.log('Summary API Response:', orders.length, 'orders');
                if (orders.length > 0) {
                    console.log('Sample order status:', orders[0]?.status);
                    console.log('Unique statuses:', [...new Set(orders.map(o => o.status))]);
                }
                setSummaryData({
                    total: orders.length,
                    pickupRequested: orders.filter(o => o.status === 'PICKUP_REQUESTED').length,
                    open: orders.filter(o => o.status === 'OPEN').length,
                    failed: orders.filter(o => o.status === 'FAILED').length,
                    cancelled: orders.filter(o => o.status === 'CANCELLED').length,
                    courierAllocated: orders.filter(o => o.status === 'COURIER_ALLOCATED').length,
                    completed: orders.filter(o => o.status === 'PICKUP_COMPLETED').length,
                    pickupFailed: orders.filter(o => o.status === 'PICKUP_FAILED').length,
                });
                console.log('Summary:', summaryData);
            }
        } catch (error) {
            console.error('Error fetching summary data:', error);
        } finally {
            setSummaryLoading(false);
        }
    };

    useEffect(() => {
        const today = new Date();
        const thirtyDaysAgo = subDays(today, 30);
        const defaultFilters = [
            { field: "createdDate", operator: "gte", value: `Date('${formatDate(thirtyDaysAgo, 'yyyy-MM-dd')} 00:00:00')` },
            { field: "createdDate", operator: "lte", value: `Date('${formatDate(today, 'yyyy-MM-dd')} 23:59:59')` }
        ];
        setAndFilters(defaultFilters);
        fetchSummaryData(defaultFilters);
    }, []); // eslint-disable-line

    const applyFilters = async (filters) => {
        console.log(filters);
        toggleBulkAllocateButton(filters.merchant);
        let appliedFilters = [];
        if (filters.courier) appliedFilters.push({ "field": "courierDetail.key", "operator": "eq", "value": filters.courier });
        if (filters.merchant) appliedFilters.push({ "field": "merchantDetail.key", "operator": "eq", "value": filters.merchant });
        if (filters.reference) appliedFilters.push({ "field": "reference", "operator": "eq", "value": filters.reference });
        if (filters.status) appliedFilters.push({ "field": "status", "operator": "in", "value": filters.status });
        if (filters.fromDate) appliedFilters.push({ field: "createdDate", operator: "gte", value: `Date('${formatDate(filters.fromDate, 'yyyy-MM-dd')} 00:00:00')` });
        if (filters.toDate) appliedFilters.push({ field: "createdDate", operator: "lte", value: `Date('${formatDate(filters.toDate, 'yyyy-MM-dd')} 23:59:59')` });
        setAndFilters(appliedFilters);
        fetchSummaryData(appliedFilters);
    };

    const toggleBulkAllocateButton = (merchant) => setIDMerchant(merchant);

    return (
        <>
            <PageTitle>PTL Pickup Requests</PageTitle>
            {(isFetching || isLoading || isExecuting || summaryLoading) && <Loader />}

            {/* ── Summary cards ─────────────────────────────────────────── */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {[
                    { count: summaryData.open,            label: 'Open',             bg: '#e0f2f1', fg: '#00695c' },
                    { count: summaryData.courierAllocated,label: 'Courier Allocated',bg: '#dcfce7', fg: '#15803d' },
                    { count: summaryData.pickupRequested, label: 'Pickup Requested', bg: '#dbeafe', fg: '#1d4ed8' },
                    { count: summaryData.completed,       label: 'Completed',        bg: '#dcfce7', fg: '#166534' },
                    { count: summaryData.pickupFailed,    label: 'Pickup Failed',    bg: '#fef2f2', fg: '#b91c1c' },
                    { count: summaryData.failed,          label: 'Failed (System)',  bg: '#fef2f2', fg: '#dc2626' },
                    { count: summaryData.cancelled,       label: 'Cancelled',        bg: '#f9fafb', fg: '#6b7280' },
                ].map(({ count, label, bg, fg }) => (
                    <Grid item xs={12} sm={6} md={1.7} key={label}>
                        <SummaryCard count={count} label={label} bg={bg} fg={fg} />
                    </Grid>
                ))}
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280', mb: 1 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#9ca3af', flexShrink: 0 }} />
                        All PTL pickup requests for the selected date range (defaults to last 30 days). Open counts exclude closed PURs (completed, failed and cancelled).
                    </Box>
                </Grid>
            </Grid>

            {/* ── Filter bar ────────────────────────────────────────────── */}
            <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <FormProvider methods={filterVal} onSubmit={handleFilterSubmit(applyFilters)}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>

                            <FilterField>
                                <FieldLabel>Merchant</FieldLabel>
                                <MerchantFilterCached merchantType={"isPTLMerchant"} fullWidth />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Courier</FieldLabel>
                                <CourierFilter filterList={"B2B"} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Reference</FieldLabel>
                                <RHFTextField name="reference" label="" placeholder="Reference Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Status</FieldLabel>
                                <RHFMultipleSelectField width="100%" name="status" label="" placeholder="Select Status" options={_.orderBy(pickupStatus, 'status')} size="small" />
                            </FilterField>

                            <FilterDateField>
                                <FieldLabel>Created Date</FieldLabel>
                                <RHFDateRangePickerField name="created_date" label="" placeholder="Select date range" size="small" maxDate={new Date()} />
                            </FilterDateField>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                                <Box component="button" type="submit"
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#1565c0', color: '#fff', '&:hover': { bgcolor: '#0d47a1' } }}>
                                    Filter
                                </Box>
                                <Box component="button" type="button"
                                    onClick={() => {
                                        const today = new Date();
                                        const thirtyDaysAgo = subDays(today, 30);
                                        filterReset({ status: "", courier: '', merchant: '', reference: '', fromDate: thirtyDaysAgo, toDate: today });
                                        const defaultFilters = [
                                            { field: "createdDate", operator: "gte", value: `Date('${formatDate(thirtyDaysAgo, 'yyyy-MM-dd')} 00:00:00')` },
                                            { field: "createdDate", operator: "lte", value: `Date('${formatDate(today, 'yyyy-MM-dd')} 23:59:59')` }
                                        ];
                                        setAndFilters(defaultFilters);
                                        fetchSummaryData(defaultFilters);
                                    }}
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#ef4444', color: '#fff', '&:hover': { bgcolor: '#dc2626' } }}>
                                    Reset
                                </Box>
                            </Box>

                        </Box>
                    </FormProvider>
                </CardContent>
            </Card>

            {/* ── Upload + create section ───────────────────────────────── */}
            <Card elevation={0} sx={{ mb: 2, borderRadius: '10px', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e' }}>Create Pickup Request</Typography>
                        <Typography sx={{ fontSize: '12px', color: '#9ca3af', mt: '2px' }}>Upload bulk orders or create individual pickup requests via form</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button variant="outlined" size="small" startIcon={<AddIcon />}
                            onClick={() => navigate("/create-pickup-order")}
                            sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'none', borderRadius: '6px', borderColor: '#1565c0', color: '#1565c0', '&:hover': { bgcolor: '#eff6ff' } }}>
                            Detailed Pickup
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<AddIcon />}
                            onClick={() => navigate("/quick-pickup-requests")}
                            sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'none', borderRadius: '6px', borderColor: '#1565c0', color: '#1565c0', '&:hover': { bgcolor: '#eff6ff' } }}>
                            Quick Pickup
                        </Button>
                    </Box>
                </Box>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ maxWidth: 480 }}>
                                <RHFFileField name='file' required={true} fileType={['xlsx']} label="Click to upload Excel file" />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <LoadingButton
                                    loading={isSubmitting}
                                    variant="contained"
                                    size="small"
                                    startIcon={<UploadIcon />}
                                    type="submit"
                                    disableElevation
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
                                >
                                    Upload
                                </LoadingButton>
                                <DownloadSampleFile fileName={"B2B_PICKUP_SAMPLE"} label='Sample' excel={"excel"} />
                                {selectedReferences.length > 0 && (
                                    <Button variant="contained" size="small" disableElevation
                                        onClick={autoAllocateFunc}
                                        sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, bgcolor: '#00695c', '&:hover': { bgcolor: '#004d40' } }}>
                                        Auto Allocate ({selectedReferences.length})
                                    </Button>
                                )}
                                {selectedReferences.length > 0 && idMerchant && (
                                    <Button variant="contained" size="small" disableElevation
                                        onClick={() => setIsCourierFilterOpen(true)}
                                        sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, bgcolor: '#4338ca', '&:hover': { bgcolor: '#3730a3' } }}>
                                        Bulk Allocate Courier
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </FormProvider>
                </CardContent>
            </Card>

            {/* ── Data table ────────────────────────────────────────────── */}
            <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <ProzoDataGrid
                    columns={columns}
                    autoHeight={true}
                    rows={(orders && orders.length > 0) ? orders : []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    rowSelectionModel={rowSelectionModel}
                    setRowSelectionModel={setRowSelectionModel}
                    setPage={setPage}
                    checkboxSelection={true}
                    isRowSelectable={(params) => !(params.row.courierDetail || ['CANCELLED', 'PICKUP_COMPLETED', 'PICKUP_FAILED'].includes(params.row.status))}
                    page={page}
                    pagination={true}
                    getRowHeight={() => 'auto'}
                    rowCount={Number.MAX_VALUE}
                    hideCountNumber={true}
                    getRowId={(row) => row.id}
                />
            </Box>

            <MUIModal
                open={isCourierFilterOpen}
                handleClose={handleClose}
                title="Select Courier Partner"
                maxWidth="md"
                children={
                    <BulkAllocateCourier
                        idMerchant={idMerchant}
                        setSelectedReferences={setSelectedReferences}
                        setRowSelectionModel={setRowSelectionModel}
                        refetch={refetch}
                        handleClose={handleClose}
                        selectedReferences={selectedReferences}
                        rowSelectionModel={rowSelectionModel}
                    />
                }
            />

            <MUIModal
                open={Boolean(confirmCompleteRow)}
                handleClose={() => setConfirmCompleteRow(null)}
                title="Mark Pickup Completed"
                maxWidth="sm"
                hideClose={true}
                children={
                    confirmCompleteRow && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                            <Typography sx={{ fontSize: 14 }}>
                                Mark pickup request <b>{confirmCompleteRow.reference}</b> as completed?
                                This closes the PUR permanently and cannot be undone.
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Button size="small" onClick={() => setConfirmCompleteRow(null)}>Cancel</Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    disabled={isExecuting}
                                    onClick={() => {
                                        handleMarkCompleted(confirmCompleteRow);
                                        setConfirmCompleteRow(null);
                                    }}
                                >
                                    Mark Completed
                                </Button>
                            </Box>
                        </Box>
                    )
                }
            />

            <MUIModal
                open={Boolean(failModalRow)}
                handleClose={() => setFailModalRow(null)}
                title="Mark Pickup Failed"
                maxWidth="sm"
                hideClose={true}
                children={
                    failModalRow && (
                        <MarkPickupFailedModal
                            row={failModalRow}
                            handleClose={() => setFailModalRow(null)}
                            onDone={refreshAfterAction}
                        />
                    )
                }
            />
        </>
    );
}

// ─── Summary Card ─────────────────────────────────────────────────────────────

const SummaryCard = ({ count, label, bg, fg }) => (
    <Card elevation={0} sx={{ bgcolor: bg, borderRadius: '10px', border: `1px solid ${fg}22`, height: '100%' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, textAlign: 'center' }}>
            <Box sx={{ fontSize: '2rem', fontWeight: 800, color: fg, lineHeight: 1.1, mb: '4px' }}>
                {count}
            </Box>
            <Box sx={{ fontSize: '10px', fontWeight: 700, color: fg, textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.3 }}>
                {label}
            </Box>
        </CardContent>
    </Card>
);

// ─── Bulk Allocate Courier modal ──────────────────────────────────────────────

const BulkAllocateCourier = ({
    idMerchant, setSelectedReferences, setRowSelectionModel,
    refetch, handleClose, selectedReferences,
}) => {
    const [whitelistCouriers, setWhitelistCouriers] = useState([]);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (idMerchant) {
            getGeneric(`${apiConstants.MERCHANT_DETAILS}/${idMerchant}`)
                .then((response) => {
                    setWhitelistCouriers(response?.data?.b2bCheapestCPList || []);
                })
                .catch((err) => { console.error(err); });
        }
    }, [idMerchant]);

    const methods = useForm();
    const { handleSubmit } = methods;
    const [bulkLoading, setBulkLoading] = useState(false);

    const onSubmit = (data) => {
        setBulkLoading(true);
        postGeneric(apiConstants.MANUAL_ALLOCATE_COURIER, {
            references: selectedReferences,
            courierPrzId: data.courier,
            merchantOid: idMerchant
        }).then(response => {
            enqueueSnackbar(response?.data?.message || response.data?.error, { variant: 'success' });
        }).catch((err) => {
            console.log(err);
            const message = err?.response?.data?.message || "Something went wrong while Allocating courier";
            enqueueSnackbar(message, { variant: "error" });
        }).finally(() => {
            setSelectedReferences([]);
            setRowSelectionModel([]);
            refetch();
            handleClose();
            setBulkLoading(false);
        });
    };

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                    <RHFSelectField width="100%" name="courier" label="Select Courier"
                        options={whitelistCouriers.map((val) => ({ value: val, label: val }))} />
                </Grid>
                <Grid item xs={12}>
                    <LoadingButton loading={bulkLoading} variant="contained" type="submit" disableElevation
                        sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}>
                        Save
                    </LoadingButton>
                </Grid>
            </Grid>
        </FormProvider>
    );
};
